import { Injectable, NgZone, inject, signal } from '@angular/core';
import { OSKColorVariant, OSKIconName } from '../types/osk-theme.types';

/**
 * Notification item managed by OSKToastService and rendered by <osk-toast-container>
 */
export interface OSKToastItem {
  id: string;
  title: string;
  description?: string;
  color: OSKColorVariant;
  icon?: OSKIconName;
  durationMs: number;
}

export interface OSKToastOptions {
  title: string;
  description?: string;
  color?: OSKColorVariant;
  icon?: OSKIconName;
  durationMs?: number;
}

/**
 * Signal-based Toast Notification Service (`@Injectable({ providedIn: 'root' })`).
 * Pair with `<osk-toast-container>` at the root of the layout.
 */
@Injectable({ providedIn: 'root' })
export class OSKToastService {
  // Toasts are often raised from callbacks running outside Angular's zone (e.g. the Firebase SDK).
  // On Angular 17, a signal write outside the zone does not schedule change detection, so updates
  // go through NgZone.run. Angular 18+ schedules change detection on signal writes: remove it then.
  private readonly ngZone = inject(NgZone);
  private counter = 0;
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly itemsSignal = signal<readonly OSKToastItem[]>([]);

  readonly toasts = this.itemsSignal.asReadonly();

  show(options: OSKToastOptions): string {
    const id = `osk-toast-${++this.counter}-${Date.now()}`;
    const color: OSKColorVariant = options.color ?? 'primary';
    const durationMs = options.durationMs ?? 4000;

    const item: OSKToastItem = {
      id,
      title: options.title,
      description: options.description,
      color,
      icon: options.icon ?? this.defaultIconForColor(color),
      durationMs
    };

    this.ngZone.run(() => {
      this.itemsSignal.update((current) => [...current, item]);

      if (durationMs > 0) {
        const timer = setTimeout(() => {
          this.dismiss(id);
        }, durationMs);
        this.timers.set(id, timer);
      }
    });

    return id;
  }

  success(title: string, description?: string, durationMs = 4000): string {
    return this.show({ title, description, color: 'success', icon: 'check-circle', durationMs });
  }

  warning(title: string, description?: string, durationMs = 4500): string {
    return this.show({ title, description, color: 'warning', icon: 'alert-triangle', durationMs });
  }

  danger(title: string, description?: string, durationMs = 5000): string {
    return this.show({ title, description, color: 'danger', icon: 'x-circle', durationMs });
  }

  info(title: string, description?: string, durationMs = 4000): string {
    return this.show({ title, description, color: 'info', icon: 'info', durationMs });
  }

  dismiss(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this.ngZone.run(() => {
      this.itemsSignal.update((current) => current.filter((t) => t.id !== id));
    });
  }

  clearAll(): void {
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }
    this.timers.clear();
    this.ngZone.run(() => {
      this.itemsSignal.set([]);
    });
  }

  private defaultIconForColor(color: OSKColorVariant): OSKIconName {
    if (color === 'success') {
      return 'check-circle';
    }
    if (color === 'warning') {
      return 'alert-triangle';
    }
    if (color === 'danger') {
      return 'x-circle';
    }
    return 'info';
  }
}
